import { ISelectItem } from "@components/inputs/select/Select.types";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import { IFormGroupDef, IFormLineItemsDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import BindingContext, { createPath, IEntity } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrPayrollSettingEntity,
    PrAccountAssignmentEntity,
    PrAccountingItemTypeEntity,
    PrPayrollSettingEntity,
} from "@odata/GeneratedEntityTypes";
import {
    PrAccountingCategoryCode,
    PrAccountingGroupSelectionCode,
    PrAccountingItemTypeCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName, getEnumValues } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { getAccountFormatter } from "@pages/accountAssignment/AccountAssignment.utils.shared";
import {
    AccountTypePath,
    renderAccountingCategoryTabs
} from "@pages/payroll/accountingSettings/AccountingSettings.utils";
import i18next from "i18next";

import { BasicInputSizes, CacheStrategy, FastEntryInputSizes, FieldType } from "../../../enums";
import { TValue } from "../../../global.types";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { IDefinition, IGetDefinition, TFieldDefinition } from "../../PageUtils";

export const AccountingTextLocalPath = BindingContext.localContext("AccountingText");

const prAccountingSettingsAccountCommonDef: TFieldDefinition = {
    type: FieldType.ComboBox,
    cacheStrategy: CacheStrategy.View,
    columns: [{
        id: "Number",
        additionalProperties: [{
            id: "/Name"
        }],
        formatter: getAccountFormatter("") // format from flatten accounts where is the prop without prefix
    }],
    formatter: (val: TValue, _args: IFormatOptions) => {
        // format as value only (AccountNumber, e.g. "015") -> we don't want to show the account name
        // as the field is too small
        return val as string;
    },
    fieldSettings: {
        entitySet: EntitySetName.FlattenAccounts,
        idName: "Number"
    },
    width: FastEntryInputSizes.XS
};

function hasFixedAccountingGroupSelection(args: IGetValueArgs): boolean {
    const itemTypeCode = args.storage.getValue(args.bindingContext.getParent())?.ItemTypeCode;
    return itemTypeCode === PrAccountingItemTypeCode.StatutoryInsuranceForCompany ||
        itemTypeCode === PrAccountingItemTypeCode.SocialSecurityEmployer;
}

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const entitySet = EntitySetName.PrPayrollSettings;

    const lineItemsDef: IFormLineItemsDef = {
        collection: PrPayrollSettingEntity.AccountAssignments,
        order: "Id",
        canReorder: false,
        canAdd: false,
        isGroupToggleable: false,
        isItemCloneable: false,
        isItemRemovable: false,
        isItemVisible: args => {
            const selectedTabId = args.storage.getValueByPath(AccountTypePath) ?? PrAccountingCategoryCode.Employee;
            return args.item.AccountingCategoryCode === selectedTabId;
        },
        columns: [
            { id: PrAccountAssignmentEntity.Name },
            { id: PrAccountAssignmentEntity.DebitAccountNumber },
            { id: PrAccountAssignmentEntity.CreditAccountNumber },
            { id: PrAccountAssignmentEntity.AccountingGroupSelection },
            { id: AccountingTextLocalPath },
        ]
    };

    const groups: IFormGroupDef[] = [{
        id: "Tabs",
        rows: [[{ id: AccountTypePath }]],
        isSmallBottomMargin: true
    }];

    const groupTypes = getEnumValues(EntityTypeName.PrAccountingItemGroup);
    groupTypes.forEach((groupType) => {
        groups.push({
            id: groupType,
            title: getEnumDisplayValue(EntityTypeName.PrAccountingItemGroup, groupType),
            isVisible: (args: IGetValueArgs) => {
                const accType = args.storage.getValueByPath(AccountTypePath) ?? PrAccountingCategoryCode.Employee;
                return !!(args.storage.data.entity as IPrPayrollSettingEntity).AccountAssignments?.find(item => {
                    return item.ItemType.ItemGroupCode === groupType && item.AccountingCategoryCode === accType;
                });
            },
            lineItems: {
                ...lineItemsDef,
                filterItems: (storage: FormStorage, id: string, items: IEntity[]): IEntity[] =>
                    items.filter(item => item.ItemType.ItemGroupCode === id)
            }
        });
    });

    const form: IFormDef = {
        id: `${EntityTypeName.PrPayrollSetting}Form`,
        translationFiles: getDefinitions.translationFiles,
        additionalProperties: [{
            id: PrPayrollSettingEntity.AccountAssignments,
            additionalProperties: [
                { id: PrAccountAssignmentEntity.AccountingCategoryCode },
                { id: PrAccountAssignmentEntity.ItemTypeCode },
                { id: createPath(PrAccountAssignmentEntity.ItemType, PrAccountingItemTypeEntity.ItemGroupCode) }
            ]
        }],
        fieldDefinition: {
            [AccountTypePath]: {
                type: FieldType.Custom,
                render: renderAccountingCategoryTabs
            },
            [createPath(PrPayrollSettingEntity.AccountAssignments, PrAccountAssignmentEntity.Name)]: {
                width: BasicInputSizes.XL
            },
            [createPath(PrPayrollSettingEntity.AccountAssignments, PrAccountAssignmentEntity.DebitAccountNumber)]: {
                ...prAccountingSettingsAccountCommonDef
            },
            [createPath(PrPayrollSettingEntity.AccountAssignments, PrAccountAssignmentEntity.CreditAccountNumber)]: {
                ...prAccountingSettingsAccountCommonDef
            },
            [createPath(PrPayrollSettingEntity.AccountAssignments, PrAccountAssignmentEntity.AccountingGroupSelection)]: {
                label: i18next.t("PrAccountingSettings:AccountingGroupSelection"),
                type: FieldType.ComboBox,
                isDisabled: hasFixedAccountingGroupSelection,
                fieldSettings: {
                    displayName: "Name",
                    itemsForRender: (items: ISelectItem[], args: IGetValueArgs) => {
                        if (hasFixedAccountingGroupSelection(args)) {
                            return items.filter(item => item.id !== PrAccountingGroupSelectionCode.PerEmployee);
                        }
                        return items;
                    }
                }
            },
            [createPath(PrPayrollSettingEntity.AccountAssignments, AccountingTextLocalPath)]: {
                label: i18next.t("PrAccountingSettings:AccountingText"),
                isReadOnly: true,
                width: BasicInputSizes.fitContent
            },
        },
        groups
    };

    return {
        entitySet,
        form
    };
};

getDefinitions.translationFiles = ["PrAccountingSettings", "Payroll", "Common", getEnumNameSpaceName(EntityTypeName.PrAccountingItemGroup)];
